import { useRequiredContext } from "@redotech/react-util/context";
import { type LoadState, useLoad } from "@redotech/react-util/load";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { REDO_MERCHANT_SERVER_URL } from "@redotech/redo-merchant-app-common/config";
import {
  createRedoOnboardingV2RpcClient,
  type RedoOnboardingV2RpcClient,
} from "@redotech/redo-onboarding-api/client";
import { useMutation } from "@tanstack/react-query";
import { createContext, memo, type ReactNode, useMemo, useState } from "react";

export type ReturnsOnboardingResumeState = Awaited<
  ReturnType<RedoOnboardingV2RpcClient["getResumeState"]>
>;

interface ReturnsOnboardingResumeLoad extends LoadState<ReturnsOnboardingResumeState> {
  archive: () => void;
  archiving: boolean;
}

export const ReturnsOnboardingResumeStateContext = createContext<
  ReturnsOnboardingResumeLoad | undefined
>(undefined);

ReturnsOnboardingResumeStateContext.displayName =
  "ReturnsOnboardingResumeStateContext";

export interface ReturnsOnboardingResumeStateProviderProps {
  children: ReactNode;
}

export const ReturnsOnboardingResumeStateProvider = memo(
  function ReturnsOnboardingResumeStateProvider({
    children,
  }: ReturnsOnboardingResumeStateProviderProps) {
    const auth = useRequiredContext(AuthContext);
    const client = useMemo(
      () =>
        createRedoOnboardingV2RpcClient({
          baseURL: new URL(`${REDO_MERCHANT_SERVER_URL}/onboarding-v2-rpc`),
          headers: { Authorization: auth.token },
        }),
      [auth.token],
    );
    const [revision, setRevision] = useState(0);
    const state = useLoad(
      (signal) => client.getResumeState({ productId: "returns" }, { signal }),
      [client, revision],
    );
    const archive = useMutation({
      meta: { showErrorToast: true },
      mutationFn: () => client.archiveReturnsOnboarding({}),
      onSuccess: () => setRevision((revision) => revision + 1),
    });

    return (
      <ReturnsOnboardingResumeStateContext.Provider
        value={{
          ...state,
          archive: archive.mutate,
          archiving: archive.isPending,
        }}
      >
        {children}
      </ReturnsOnboardingResumeStateContext.Provider>
    );
  },
);

export function useReturnsOnboardingResumeState(): ReturnsOnboardingResumeLoad {
  return useRequiredContext(ReturnsOnboardingResumeStateContext);
}
